import React, { useState, useEffect, useMemo } from 'react';
import { Card, Badge, Spinner, Dropdown, Form } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { formatCurrency } from '../../utils/formatUtils';
import jwt_decode from 'jwt-decode';
import MultiSelectDropdown from './MultiSelectDropdown1';
import './css/KanbanView.css';

const KanbanView = ({
  records,
  kanbanConfig,
  objectName,
  onUpdateRecord,
  loading,
  picklistOptions, // Pass picklist options for the groupBy field
  onEdit,           // Callback for edit action
  onDelete,         // Callback for delete action
  onClone,          // Callback for clone action
  onView            // Callback for view action
}) => {
  const [columns, setColumns] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [selectedStageFilters, setSelectedStageFilters] = useState([]);

  // Resolve currency with priority: token currency (user currency or company currency) > default
  const resolveCurrency = (fallbackCurrency = 'INR') => {
    // Check currency from JWT token
    // Token currency is COALESCE(u.currency, c.currency) from auth.model.js
    // So it will be user.currency if available, otherwise company.currency
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwt_decode(token);
        // Currency in token is: user.currency if available, otherwise company.currency
        const tokenCurrency = decoded.currency;
        if (tokenCurrency) {
          return tokenCurrency;
        }
      }
    } catch (error) {
      console.error('Error decoding token for currency:', error);
    }

    // Fallback to default
    return fallbackCurrency;
  };

  // Group records by the kanban field
  useEffect(() => {
    if (!records || !kanbanConfig?.groupByField) return;

    const groupedRecords = {};

    // Initialize columns from ALL picklist options in the correct sort order
    // This ensures all values show up as columns, even if they have 0 records
    if (picklistOptions && picklistOptions.length > 0) {
      // Picklist options are already sorted by sort_order from the backend
      picklistOptions.forEach(option => {
        const valueKey = option.key || option.value;
        groupedRecords[valueKey] = {
          label: option.label || option.value,
          value: valueKey,
          records: [],
          color: option.color || '#6c757d',
          sortOrder: option.sort_order || 0
        };
      });
    }

    // Group records into the initialized columns
    records.forEach(record => {
      const fieldValue = record[kanbanConfig.groupByField]?.value;

      // Only add to columns if the value exists in picklist options
      // This prevents "uncategorized" columns for invalid/deleted values
      if (fieldValue && groupedRecords[fieldValue]) {
        groupedRecords[fieldValue].records.push(record);
      }
    });

    // Sort records within each column if configured
    if (kanbanConfig.sortBy) {
      Object.keys(groupedRecords).forEach(key => {
        groupedRecords[key].records.sort((a, b) => {
          const aVal = a[kanbanConfig.sortBy]?.value;
          const bVal = b[kanbanConfig.sortBy]?.value;

          // Handle null/undefined values
          if (!aVal && !bVal) return 0;
          if (!aVal) return 1;
          if (!bVal) return -1;

          // Try date comparison first
          const aDate = new Date(aVal);
          const bDate = new Date(bVal);
          if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
            return kanbanConfig.sortOrder === 'asc'
              ? aDate.getTime() - bDate.getTime()
              : bDate.getTime() - aDate.getTime();
          }

          // Try numeric comparison
          const aNum = parseFloat(aVal);
          const bNum = parseFloat(bVal);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return kanbanConfig.sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
          }

          // String comparison
          const comparison = String(aVal).localeCompare(String(bVal));
          return kanbanConfig.sortOrder === 'asc' ? comparison : -comparison;
        });
      });
    }

    console.log('Kanban columns initialized:', {
      totalColumns: Object.keys(groupedRecords).length,
      columns: Object.keys(groupedRecords).map(key => ({
        key,
        label: groupedRecords[key].label,
        sortOrder: groupedRecords[key].sortOrder,
        recordCount: groupedRecords[key].records.length
      }))
    });

    setColumns(groupedRecords);
  }, [records, kanbanConfig, picklistOptions]);

  // Filter columns based on selected stage filters
  const filteredColumns = useMemo(() => {
    if (!selectedStageFilters || selectedStageFilters.length === 0) {
      return columns;
    }

    const filtered = {};
    Object.keys(columns).forEach(key => {
      if (selectedStageFilters.includes(key)) {
        filtered[key] = columns[key];
      }
    });
    return filtered;
  }, [columns, selectedStageFilters]);

  // Transform picklist options for MultiSelectDropdown
  const transformedPicklistOptions = useMemo(() => {
    if (!picklistOptions || picklistOptions.length === 0) return [];
    return picklistOptions.map(option => ({
      key: option.key || option.value,
      value: option.label || option.value
    }));
  }, [picklistOptions]);

  // Initialize selected filters with all values when picklist options change
  useEffect(() => {
    if (transformedPicklistOptions.length > 0 && selectedStageFilters.length === 0) {
      // Select all by default
      setSelectedStageFilters(transformedPicklistOptions.map(opt => opt.key));
    }
  }, [transformedPicklistOptions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest('.dropdown')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  // Close any open dropdowns when overlay appears
  useEffect(() => {
    if (isUpdating && activeDropdown) {
      setActiveDropdown(null);
    }
  }, [isUpdating]);

  // Handle dropdown toggle
  const handleDropdownToggle = (recordId, isOpen) => {
    console.log('Dropdown toggle:', recordId, isOpen);
    if (isOpen) {
      setActiveDropdown(recordId);
    } else {
      setActiveDropdown(null);
    }
  };

  // Action handlers
  const handleViewRecord = (record) => {
    const recordIdValue = record.id?.value || record.id;
    if (onView) {
      onView(record);
    } else if (recordIdValue) {
      window.open(`/view/${objectName}/${recordIdValue}`, '_blank');
    }
  };

  const handleEditRecord = (record) => {
    if (onEdit) {
      onEdit(record);
    }
  };

  const handleCloneRecord = (record) => {
    if (onClone) {
      onClone(record);
    }
  };

  const handleDeleteRecord = (record) => {
    if (onDelete) {
      onDelete(record);
    }
  };

  // Handle drag and drop
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside the list
    if (!destination) return;

    // No movement
    if (source.droppableId === destination.droppableId &&
      source.index === destination.index) {
      return;
    }

    // Update record status
    setIsUpdating(true);
    try {
      const recordId = draggableId;
      const newStatus = destination.droppableId;

      // Call API to update the record
      await onUpdateRecord(recordId, {
        [kanbanConfig.groupByField]: newStatus
      });

      // Update local state optimistically
      const newColumns = { ...columns };
      const sourceColumn = { ...newColumns[source.droppableId] };
      const destColumn = { ...newColumns[destination.droppableId] };

      // Create new arrays to avoid mutation
      sourceColumn.records = [...sourceColumn.records];
      destColumn.records = [...destColumn.records];

      // Remove from source
      const [movedRecord] = sourceColumn.records.splice(source.index, 1);

      // Add to destination
      destColumn.records.splice(destination.index, 0, movedRecord);

      // Update the record's field value
      movedRecord[kanbanConfig.groupByField] = {
        ...movedRecord[kanbanConfig.groupByField],
        value: newStatus,
        displayValue: destColumn.label
      };

      // Update columns
      newColumns[source.droppableId] = sourceColumn;
      newColumns[destination.droppableId] = destColumn;

      setColumns(newColumns);
    } catch (error) {
      console.error('Error updating record:', error);
      // Refresh to get correct state
      window.location.reload();
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to get color based on field value
  const getColorForValue = (record, fieldName) => {
    if (!fieldName || !record[fieldName]) return '#6c757d';

    const value = record[fieldName].value;
    if (!value) return '#6c757d';

    const valueStr = String(value).toLowerCase();

    // Priority colors
    const priorityColors = {
      'high': '#dc3545',
      'critical': '#dc3545',
      'medium': '#ffc107',
      'normal': '#28a745',
      'low': '#28a745'
    };

    // Status colors
    const statusColors = {
      'new': '#17a2b8',
      'open': '#17a2b8',
      'in_progress': '#007bff',
      'in progress': '#007bff',
      'pending': '#ffc107',
      'completed': '#28a745',
      'closed': '#28a745',
      'cancelled': '#6c757d',
      'canceled': '#6c757d'
    };

    // Check priority colors first
    if (priorityColors[valueStr]) return priorityColors[valueStr];

    // Check status colors
    if (statusColors[valueStr]) return statusColors[valueStr];

    // For numeric values (like amounts), use gradient
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      if (numValue > 100000) return '#28a745'; // Green
      if (numValue > 50000) return '#ffc107';  // Yellow
      if (numValue > 10000) return '#17a2b8';  // Blue
      return '#6c757d'; // Gray
    }

    return '#6c757d'; // Default gray
  };

  // Render a single card
  const renderCard = (record, index) => {
    const cardFields = kanbanConfig.cardFields || [];
    const colorField = kanbanConfig.colorField;

    // Determine card color based on colorField
    const cardColor = getColorForValue(record, colorField);

    // Get the record ID for hyperlink
    const recordIdValue = record.id?.value || record.id;

    // Determine the title field and value
    const titleValue = record.name?.displayValue || record.name?.value ||
      record.subject?.displayValue || record.subject?.value || 'Untitled';

    // Check if we have a name or subject field (for hyperlink)
    const hasNameField = record.name !== undefined;
    const hasSubjectField = record.subject !== undefined;

    return (
      <Draggable key={recordIdValue} draggableId={recordIdValue} index={index}>
        {(provided, snapshot) => (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`kanban-card mb-2 ${snapshot.isDragging ? 'dragging' : ''} ${activeDropdown === recordIdValue ? 'dropdown-active' : ''}`}
            style={{
              ...provided.draggableProps.style,
              borderLeft: `4px solid ${cardColor}`,
              opacity: snapshot.isDragging ? 0.9 : 1,
              zIndex: snapshot.isDragging ? 1000 : (activeDropdown === recordIdValue ? 1001 : 'auto'),
              position: 'relative',
              cursor: snapshot.isDragging ? 'grabbing' : 'grab'
            }}
          >
            <Card.Body
              className="p-3"
              style={{ position: 'relative' }}
            >
              {/* Actions Dropdown - Top Right Corner */}
              <div
                className="kanban-card-actions"
                onMouseDown={(e) => {
                  e.stopPropagation(); // Prevent drag when clicking dropdown area
                }}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent drag when clicking dropdown
                }}
                style={{
                  display: isUpdating ? 'none' : 'block' // Hide dropdown arrow when overlay is active
                }}
              >
                <Dropdown
                  onToggle={(isOpen) => handleDropdownToggle(recordIdValue, isOpen)}
                  show={activeDropdown === recordIdValue}
                  className="kanban-actions-dropdown"
                >
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    id={`kanban-dropdown-${recordIdValue}`}
                    className="kanban-card-dropdown-toggle"
                    style={{
                      padding: '2px 8px',
                      fontSize: '14px',
                      border: '1px solid #dee2e6',
                      backgroundColor: 'white',
                      color: '#6c757d',
                      lineHeight: 1
                    }}
                  >
                    <span style={{ color: '#6c757d', fontWeight: 'bold' }}>&#9660;</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end" className="kanban-dropdown-menu">
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleViewRecord(record);
                      }}
                    >
                      <i className="fas fa-eye me-2"></i>
                      View
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleEditRecord(record);
                      }}
                    >
                      <i className="fas fa-edit me-2"></i>
                      Edit
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleCloneRecord(record);
                      }}
                    >
                      <i className="fas fa-clone me-2"></i>
                      Clone
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveDropdown(null);
                        handleDeleteRecord(record);
                      }}
                      className="text-danger"
                    >
                      <i className="fas fa-trash me-2"></i>
                      Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {/* Card Title - clickable hyperlink to record detail page (like name field in table) */}
              <div className="kanban-card-title mb-2" style={{ paddingRight: '30px' }}>
                {recordIdValue && (hasNameField || hasSubjectField) ? (
                  <a
                    href={`/view/${objectName}/${recordIdValue}`}
                    className="table-link"
                    style={{
                      color: '#032d60',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation(); // Prevent drag when clicking link
                    }}
                    onClick={(e) => {
                      // Allow navigation - don't stop propagation on click
                      // The mousedown stopPropagation is enough to prevent drag
                    }}
                  >
                    {titleValue}
                  </a>
                ) : (
                  <strong>{titleValue}</strong>
                )}
              </div>

              {/* Additional Fields - Only show fields that are in cardFields config */}
              {cardFields.map(fieldName => {
                // Skip name/subject as they're shown as title
                if (fieldName === 'name' || fieldName === 'subject') return null;

                // Skip id field - it will be handled separately only if configured
                if (fieldName === 'id') return null;

                if (!record[fieldName]) return null;

                const field = record[fieldName];
                let displayValue = field.displayValue || field.value;

                // Skip if no value
                if (!displayValue) return null;

                return (
                  <div key={fieldName} className="kanban-card-field mb-1">
                    <small className="text-muted fw-bold">
                      {field.label}:
                    </small>
                    <span className="ms-1" style={{ fontSize: '13px' }}>
                      {displayValue}
                    </span>
                  </div>
                );
              })}

              {/* Record ID Badge - Only show if 'id' is in cardFields configuration */}
              {cardFields.includes('id') && (
                <div className="mt-2">
                  <Badge bg="light" text="dark" style={{ fontSize: '10px' }}>
                    ID: {record.id?.displayValue || record.id?.value || 'N/A'}
                  </Badge>
                </div>
              )}
            </Card.Body>
          </Card>
        )}
      </Draggable>
    );
  };

  // Calculate aggregation for summary fields
  const calculateAggregation = (records, fieldName, aggregation) => {
    // For count, always return 0 if no records, otherwise return count
    if (aggregation.toLowerCase() === 'count') {
      return records && records.length > 0 ? records.length : 0;
    }

    if (!records || records.length === 0) return 0;

    const values = records
      .map(record => {
        const field = record[fieldName];
        if (!field) return null;

        // Handle enriched data structure
        const value = field.value !== undefined ? field.value : field;
        if (value === null || value === undefined || value === '') return null;

        // Try to parse as number
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) return numValue;

        // Try to parse as date
        const dateValue = new Date(value);
        if (!isNaN(dateValue.getTime())) return dateValue.getTime();

        return null;
      })
      .filter(val => val !== null);

    if (values.length === 0) return 0;

    switch (aggregation.toLowerCase()) {
      case 'sum':
        return values.reduce((sum, val) => sum + val, 0);
      case 'avg':
      case 'average':
        return values.reduce((sum, val) => sum + val, 0) / values.length;
      case 'min':
        return Math.min(...values);
      case 'max':
        return Math.max(...values);
      case 'count':
        return records.length;
      default:
        return 0;
    }
  };

  // Check if a field is a currency field
  const isCurrencyField = (fieldName) => {
    if (!fieldName || !records || records.length === 0) return false;
    const field = records[0]?.[fieldName];
    if (!field) return false;

    // Check field type (from enriched data structure)
    const fieldType = field.type?.toLowerCase();
    if (fieldType === 'currency') return true;

    // Check dataType if available
    const dataType = field.dataType?.toLowerCase();
    if (dataType === 'currency') return true;

    // Check field name patterns as fallback
    const fieldNameLower = fieldName.toLowerCase();
    const currencyPatterns = ['amount', 'price', 'cost', 'revenue', 'total', 'value', 'fee', 'charge'];
    return currencyPatterns.some(pattern => fieldNameLower.includes(pattern));
  };

  // Check if a field is a date/datetime field
  const isDateField = (fieldName) => {
    if (!fieldName || !records || records.length === 0) return false;
    const field = records[0]?.[fieldName];
    if (!field) return false;

    // Check field type
    const fieldType = field.type?.toLowerCase();
    if (fieldType === 'date' || fieldType === 'datetime') return true;

    // Check dataType if available
    const dataType = field.dataType?.toLowerCase();
    if (dataType === 'date' || dataType === 'datetime') return true;

    return false;
  };

  // Format aggregated value for display
  const formatAggregatedValue = (value, fieldName, aggregation) => {
    // Show 0 instead of N/A for null/undefined values to maintain alignment
    if (value === null || value === undefined) {
      if (aggregation === 'count') return '0';
      // For numeric aggregations, return formatted 0
      if (isCurrencyField(fieldName)) {
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(0, currencyCode, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }
      return '0';
    }

    if (aggregation === 'count') {
      return value.toString();
    }

    // Format as number
    if (typeof value === 'number') {
      // Check if it's a date field first (for min/max on dates)
      if ((aggregation === 'min' || aggregation === 'max') && isDateField(fieldName)) {
        // For date fields, the value might be a timestamp
        const dateValue = new Date(value);
        if (!isNaN(dateValue.getTime()) && dateValue.getTime() > 0) {
          return dateValue.toLocaleDateString();
        }
      }

      // Check if it's a currency field and use proper currency formatting
      if (isCurrencyField(fieldName)) {
        const currencyCode = resolveCurrency('INR');
        return formatCurrency(value, currencyCode, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }
      // Format with commas for large numbers
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value);
    }

    return value.toString();
  };

  // Render a column
  const renderColumn = (columnId, column) => {
    const recordCount = column.records.length;
    const showCount = kanbanConfig.showRecordCount !== false; // Default true
    const summaryFields = kanbanConfig.summaryFields || [];

    // Calculate summary totals for display at top
    const summaryTotals = summaryFields
      .filter(sf => sf.field && sf.aggregation)
      .map(summaryField => {
        const aggregatedValue = calculateAggregation(
          column.records,
          summaryField.field,
          summaryField.aggregation
        );
        return {
          ...summaryField,
          value: aggregatedValue,
          formattedValue: formatAggregatedValue(aggregatedValue, summaryField.field, summaryField.aggregation)
        };
      });

    return (
      <div key={columnId} className="kanban-column">
        <div className="kanban-column-header">
          <div className="d-flex align-items-center gap-2">
            <h6 className="mb-0">{column.label}</h6>
            {showCount && (
              <Badge bg="secondary" pill>{recordCount}</Badge>
            )}
          </div>
        </div>

        {/* Summary Totals - Below stage name, above cards */}
        {summaryTotals.length > 0 && (
          <div
            className="kanban-column-summary-totals"
            style={{
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6'
            }}
          >
            {summaryTotals.map((summaryTotal, index) => {
              // For currency fields with sum aggregation, show prominently
              if (isCurrencyField(summaryTotal.field) && summaryTotal.aggregation === 'sum') {
                return (
                  <div
                    key={index}
                    className="kanban-summary-total-currency"
                    style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: '#28a745',
                      marginBottom: index < summaryTotals.length - 1 ? '4px' : '0'
                    }}
                  >
                    {summaryTotal.formattedValue}
                  </div>
                );
              }
              // For other aggregations, show smaller
              return (
                <div
                  key={index}
                  className="kanban-summary-total-other"
                  style={{
                    fontSize: '13px',
                    color: '#6c757d',
                    marginBottom: index < summaryTotals.length - 1 ? '2px' : '0'
                  }}
                >
                  <span style={{ fontWeight: '600' }}>
                    {column.records[0]?.[summaryTotal.field]?.label || summaryTotal.field} ({summaryTotal.aggregation === 'avg' ? 'Avg' : summaryTotal.aggregation === 'sum' ? 'Sum' : summaryTotal.aggregation === 'min' ? 'Min' : summaryTotal.aggregation === 'max' ? 'Max' : summaryTotal.aggregation}): {summaryTotal.formattedValue}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <Droppable droppableId={columnId}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`kanban-column-content ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
            >
              {column.records.map((record, index) => renderCard(record, index))}
              {provided.placeholder}

              {recordCount === 0 && !snapshot.isDraggingOver && (
                <div className="text-center text-muted p-4">
                  <i className="fas fa-inbox" style={{ fontSize: '32px', opacity: 0.3 }}></i>
                  <p className="mt-2 mb-0" style={{ fontSize: '13px' }}>No items</p>
                </div>
              )}
            </div>
          )}
        </Droppable>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading Kanban view...</p>
      </div>
    );
  }

  if (!kanbanConfig?.groupByField) {
    return (
      <div className="text-center p-5">
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Kanban Configuration Missing</strong>
          <p className="mb-0 mt-2">
            Please configure a "Group By" field in the list view settings to enable Kanban view.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Stage Filter - Multi-select dropdown - Outside scrollable area */}
      {kanbanConfig?.groupByField && transformedPicklistOptions.length > 0 && (
        <div className="kanban-stage-filter" style={{
          padding: '12px 20px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          width: '100%',
          boxSizing: 'border-box',
          flexShrink: 0
        }}>
          <label style={{
            fontWeight: '600',
            color: '#032d60',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            margin: 0,
            flexShrink: 0
          }}>
            Filter Stages:
          </label>
          <div style={{ minWidth: '250px', maxWidth: '400px' }}>
            <MultiSelectDropdown
              selectedValues={selectedStageFilters}
              picklistValues={transformedPicklistOptions}
              onChange={setSelectedStageFilters}
              placeholder="Select stages to view..."
              disabled={loading}
            />
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setSelectedStageFilters(transformedPicklistOptions.map(opt => opt.key));
            }}
            disabled={loading || selectedStageFilters.length === transformedPicklistOptions.length}
            style={{
              flexShrink: 0,
              whiteSpace: 'nowrap'
            }}
          >
            Show All
          </button>
          {selectedStageFilters.length > 0 && (
            <Badge bg="info" style={{ fontSize: '12px', padding: '4px 8px', flexShrink: 0 }}>
              {selectedStageFilters.length} of {transformedPicklistOptions.length} selected
            </Badge>
          )}
        </div>
      )}

      <div className="kanban-view" style={{ position: 'relative' }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="kanban-board" style={{ position: 'relative' }}>
            {/* Render filtered columns in sort order from picklist configuration */}
            {Object.entries(filteredColumns)
              .sort((a, b) => (a[1].sortOrder || 0) - (b[1].sortOrder || 0))
              .map(([columnId, column]) =>
                renderColumn(columnId, column)
              )}

            {isUpdating && (
              <div className="kanban-updating-overlay">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-white fw-bold">Updating...</p>
              </div>
            )}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
};

export default KanbanView;
